import { FC, useEffect } from 'react';
import {
	Button,
	Dialog,
	DialogContent,
	DialogContentText,
	DialogActions,
	DialogTitle,
} from '@mui/material';
import { ProviderCaseQueue } from 'type-declarations';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useForm } from 'react-hook-form';
import { checkIsDisabled, handleSubmission, setFormFields } from './HolidayScheduleDialogUtils';
import { defaultValues, FormData, HolidayScheduleSchema } from './constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DefaultInputs from './components/DefaultInputs';
import DailyInput from './components/DailyInput';
import CalendarInputs from './components/CalendarInputs';
import DayOfWeekInput from './components/DayOfWeekInput';
import DayOfMonthInput from './components/DayOfMonthInput';
import YearlyInput from './components/YearlyInput';
import './styles.scss';

type Props = {
	open: boolean;
	selectedEditRowRecord: ProviderCaseQueue;
	// Actions are props passed into the <SimpleCrudTable />
	createAction: () => void;
	editAction: () => void;
	cancelAction: () => void;
};

const HolidayScheduleDialog: FC<Props> = ({
	open,
	selectedEditRowRecord,
	// Actions are props passed into the <SimpleCrudTable />
	createAction,
	editAction,
	cancelAction,
}) => {
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		reset,
		trigger,
		formState: { errors },
	} = useForm({
		defaultValues,
		resolver: zodResolver(HolidayScheduleSchema),
		mode: 'all',
	});
	const formWatch: FormData = watch();

	// When a schedule object is provided update the form with existing values
	useEffect(() => {
		if (selectedEditRowRecord?.id) {
			setFormFields(selectedEditRowRecord, setValue);
		}
	}, [selectedEditRowRecord]);

	return (
		<>
			<Dialog open={open} className="holiday-schedule-dialog">
				<DialogTitle>Add Holiday Schedule</DialogTitle>
				<form
					onSubmit={handleSubmit((): void => {
						handleSubmission(formWatch, selectedEditRowRecord, createAction, editAction, reset);
					})}
				>
					<DialogContent>
						<DialogContentText>Save or Edit Holiday Schedule</DialogContentText>
						{/* Inputs used by all form layouts */}
						<DefaultInputs
							formWatch={formWatch}
							control={control}
							errors={errors}
							selectedEditRowRecord={selectedEditRowRecord}
							trigger={trigger}
							setValue={setValue}
						/>
						{/* Inputs used for specific recurrence types */}
						<DailyInput
							formWatch={formWatch}
							control={control}
							errors={errors}
							trigger={trigger}
							setValue={setValue}
						/>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<CalendarInputs
								formWatch={formWatch}
								control={control}
								errors={errors}
								trigger={trigger}
								setValue={setValue}
							/>
							<DayOfWeekInput formWatch={formWatch} control={control} setValue={setValue} />
							<DayOfMonthInput
								formWatch={formWatch}
								control={control}
								errors={errors}
								setValue={setValue}
							/>
							<YearlyInput formWatch={formWatch} control={control} setValue={setValue} />
						</LocalizationProvider>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								cancelAction(); // CRUD table action
								reset(); // Resets local form
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={checkIsDisabled(formWatch, errors)}>
							Save
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
};

export default HolidayScheduleDialog;
